import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useServices } from "../useHook";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { Trash2, Upload } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

const ServiceDetails = () => {
  const { getServiceById, updateService, getServiceCategoriesForSelect } = useServices();
  const [serviceData, setServiceData] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    customUrl: "",
    description: "",
    categoryId: "",
    image: null,
    imagePreview: null,
    video: null,
    videoLabel: "",
    galleryFiles: [],
  });
  const [existingGallery, setExistingGallery] = useState([]);
  const MAX_GALLERY = 5;

  useEffect(() => {
    if (id) {
      getServiceById(id, setServiceData);
    }
  }, [id]);

  useEffect(() => {
    getServiceCategoriesForSelect(setServiceCategories).catch((error) => {
      console.error("Error fetching service categories:", error);
    });
  }, []);

  useEffect(() => {
    if (serviceData) {
      const customUrl = serviceData.custom_url ?? serviceData.customUrl ?? "";
      setFormData({
        title: serviceData.title || "",
        customUrl: customUrl.replace(/\/$/, ""),
        description: serviceData.description || "",
        categoryId:
          serviceData.category_id !== undefined && serviceData.category_id !== null
            ? String(serviceData.category_id)
            : serviceData.categoryId ?? "",
        image: null,
        imagePreview: null,
        video: null,
        videoLabel: "",
        galleryFiles: [],
      });
      setExistingGallery(Array.isArray(serviceData.images) ? [...serviceData.images] : []);
    }
  }, [serviceData]);

  useEffect(() => {
    if (showEditModal && serviceData?.images) setExistingGallery([...serviceData.images]);
  }, [showEditModal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleQuillChange = (value) => {
    setFormData({ ...formData, description: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        video: file,
        videoLabel: file.name,
      });
    }
  };

  const removeNewImage = () => {
    if (formData.imagePreview && formData.imagePreview.startsWith("blob:"))
      URL.revokeObjectURL(formData.imagePreview);
    setFormData({
      ...formData,
      image: null,
      imagePreview: null,
    });
  };

  const removeNewVideo = () => {
    setFormData({ ...formData, video: null, videoLabel: "" });
  };

  const addGalleryFile = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const current = formData.galleryFiles;
    const remaining = MAX_GALLERY - current.length;
    const toAdd = files.slice(0, remaining);
    if (toAdd.length) setFormData({ ...formData, galleryFiles: [...current, ...toAdd] });
    e.target.value = "";
  };

  const removeGalleryFile = (index) => {
    setFormData({ ...formData, galleryFiles: formData.galleryFiles.filter((_, i) => i !== index) });
  };

  const removeExistingGalleryImage = (index) => {
    setExistingGallery((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Title is required.",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const apiData = new FormData();
      apiData.append("title", formData.title.trim());
      if (formData.customUrl?.trim())
        apiData.append("customUrl", formData.customUrl.trim());
      if (formData.description)
        apiData.append("description", formData.description);
      apiData.append("categoryId", formData.categoryId ?? "");
      if (formData.image) apiData.append("image", formData.image);
      if (formData.video) apiData.append("video", formData.video);
      apiData.append(
        "existing_images",
        JSON.stringify(
          existingGallery.map((img) =>
            typeof img === "object" && img?.image_url ? { image_url: img.image_url } : img
          )
        )
      );
      formData.galleryFiles.forEach((file) => apiData.append("images", file));

      await updateService(id, apiData);
      await getServiceById(id, setServiceData);
      setShowEditModal(false);
      removeNewImage();

      Swal.fire({
        icon: "success",
        title: "Service Updated",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update service. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const imageUrl = (path) => {
    if (!path) return null;
    return path.startsWith("http") ? path : `${BASE_URL.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  useEffect(() => {
    return () => {
      if (formData.imagePreview && formData.imagePreview.startsWith("blob:"))
        URL.revokeObjectURL(formData.imagePreview);
    };
  }, [formData.imagePreview]);

  if (!serviceData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl font-semibold">Loading service details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-1">Service Details</h2>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => navigate("/services")}
            className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded"
          >
            Back to List
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            Edit
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto my-6 p-6 bg-white shadow-lg rounded-lg">
        <div className="mb-6">
          <h2 className="text-3xl font-bold">{serviceData.title}</h2>
          {(serviceData.custom_url || serviceData.customUrl) && (
            <p className="text-gray-600 mt-1">
              Custom URL: <span className="font-medium">{serviceData.custom_url || serviceData.customUrl}</span>
            </p>
          )}
          <p className="text-sm text-gray-500 mt-2">
            Created:{" "}
            {serviceData.created_at
              ? new Date(serviceData.created_at).toLocaleDateString()
              : "—"}
          </p>
          {serviceData.updated_at && (
            <p className="text-sm text-gray-500">
              Updated: {new Date(serviceData.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>

        {serviceData.image && (
          <div className="mb-6">
            <p className="font-semibold text-gray-700 mb-2">Image</p>
            <img
              src={imageUrl(serviceData.image)}
              alt={serviceData.title}
              className="rounded shadow-md max-h-80 object-contain"
            />
          </div>
        )}

        {serviceData.video && (
          <div className="mb-6">
            <p className="font-semibold text-gray-700 mb-2">Video</p>
            <video
              src={imageUrl(serviceData.video)}
              controls
              className="rounded shadow-md max-w-full max-h-96"
            />
          </div>
        )}

        {serviceData.images?.length > 0 && (
          <div className="mb-6">
            <p className="font-semibold text-gray-700 mb-2">Gallery</p>
            <div className="flex flex-wrap gap-2">
              {serviceData.images.map((img, idx) => {
                const url = typeof img === "object" ? img.image_url : img;
                return (
                  <img
                    key={img.id ?? idx}
                    src={imageUrl(url)}
                    alt=""
                    className="rounded shadow-md h-32 w-32 object-cover"
                  />
                );
              })}
            </div>
          </div>
        )}

        {serviceData.description && (
          <div className="mt-6">
            <p className="font-semibold text-gray-700 mb-2">Description</p>
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: serviceData.description }}
            />
          </div>
        )}
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Edit Service</h2>
            </div>
            <div className="overflow-y-auto flex-1">
              <form onSubmit={handleSubmit} className="p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom URL
                  </label>
                  <input
                    type="text"
                    name="customUrl"
                    value={formData.customUrl}
                    onChange={handleChange}
                    placeholder="e.g. consultation or /services/consultation"
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">No category</option>
                    {Array.isArray(serviceCategories) && serviceCategories.length > 0 ? (
                      serviceCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        {serviceCategories.length === 0 ? "Loading..." : "No categories"}
                      </option>
                    )}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <div className="border rounded-md">
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={handleQuillChange}
                      className="h-40"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image (optional, leave empty to keep current)
                  </label>
                  <div className="border border-dashed border-gray-300 p-4 rounded-md">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="detail-service-image"
                    />
                    <label
                      htmlFor="detail-service-image"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload size={24} className="text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500">
                        Click to upload new image
                      </span>
                    </label>
                    {(formData.imagePreview || formData.image) && (
                      <div className="mt-2 relative inline-block">
                        <img
                          src={
                            formData.imagePreview ||
                            (formData.image &&
                              URL.createObjectURL(formData.image))
                          }
                          alt="Preview"
                          className="h-24 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={removeNewImage}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                    {!formData.image && serviceData.image && (
                      <p className="mt-2 text-sm text-gray-500">
                        Current image is set
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video (optional, leave empty to keep current)
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700"
                  />
                  {(formData.videoLabel || serviceData.video) && (
                    <p className="mt-1 text-sm text-gray-600">
                      {formData.videoLabel || "Current video attached"}
                      {formData.videoLabel && (
                        <button
                          type="button"
                          onClick={removeNewVideo}
                          className="ml-2 text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gallery images (optional, max {MAX_GALLERY})
                  </label>
                  {existingGallery.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {existingGallery.map((img, idx) => {
                        const url = typeof img === "object" ? img.image_url : img;
                        const fullUrl = url?.startsWith("http")
                          ? url
                          : `${BASE_URL.replace(/\/$/, "")}${url?.startsWith("/") ? "" : "/"}${url}`;
                        return (
                          <div key={idx} className="relative inline-block">
                            <img src={fullUrl} alt="" className="h-20 w-20 object-cover rounded border" />
                            <button
                              type="button"
                              onClick={() => removeExistingGalleryImage(idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {formData.galleryFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.galleryFiles.map((file, idx) => (
                        <div key={idx} className="relative inline-block">
                          <img src={URL.createObjectURL(file)} alt="" className="h-20 w-20 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => removeGalleryFile(idx)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {formData.galleryFiles.length < MAX_GALLERY && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={addGalleryFile}
                        className="hidden"
                        id="detail-service-gallery"
                      />
                      <label
                        htmlFor="detail-service-gallery"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 cursor-pointer hover:underline"
                      >
                        <Upload size={16} /> Add gallery image ({formData.galleryFiles.length}/{MAX_GALLERY})
                      </label>
                    </>
                  )}
                </div>

                <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${
                      isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Updating...
                      </>
                    ) : (
                      "Update"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceDetails;
